// apps/share-web/src/song-text-api.ts
//
// Слова ОДНОЙ песни: прочитать (`GET`), переключить, кто их видит (`PATCH`),
// снять с публикации (`DELETE`) — всё на адресе `/api/song-texts/:id`.
//
// Маршрут за логином — но уже НЕ безусловно. До волны F здесь стояло «без
// cookie маршрут всегда ответит 401», и с задачи 3 этой волны это неправда:
// слова, которые владелец назвал публичными (`visibility === 'public'`,
// миграция 0019), читаются БЕЗ аккаунта — сравнение на стороне маршрута
// строгое, поэтому «публично» бывает только по явному действию владельца, а
// всё остальное, включая NULL у каждой песни, опубликованной до 0019, —
// по-прежнему за логином и членством в команде владельца.
//
// `credentials: 'include'` остаётся у ВСЕХ трёх запросов, и именно потому, что
// публичная ветка существует: cookie — это то, чем товарищ по команде читает
// НЕпубличные слова, а анониму она просто не мешает. Запись (PATCH/DELETE) без
// неё не работает никогда: обе — действия владельца.
//
// Лестница отказов сверена с doc-комментарием боевого маршрута
// (`apps/share-worker/functions/api/song-texts/[id].ts`, onRequestGet):
//   401                  → auth     — нет валидной сессии
//   403 not_a_member     → forbidden — строка есть, читающий не в команде
//   404 not_found        → gone     — такой строки нет вовсе
//   404 payload_missing  → gone     — строка жива, но объект в R2 потерян
// forbidden и gone РАЗЛИЧАЮТСЯ намеренно: «тебя нет в команде» и «публикацию
// сняли» — разные вещи для музыканта, и это единственный слой, который вообще
// видит разницу между ними (сервер отвечает РАЗНЫМИ статусами — 403 против
// 404 — то есть уже провёл эту границу; схлопнуть их здесь означало бы
// потерять то, что маршрут специально сообщил).
// Любой другой статус, брошенное исключение или неразборное тело — network:
// это не про то, что случилось, а про то, что стоит попробовать ещё раз.

import type { TextShareSong } from '@bandway/share-types';

/**
 * Нагрузка слов одной песни — ТОТ ЖЕ тип, что везёт текстовая ссылка на
 * сетлист (`TextShareSong`). `songEntry` (app/storage/share-text-payload.js)
 * кладёт в R2 ровно эту форму для обоих маршрутов публикации, поэтому здесь
 * не собственная копия контракта, а прямой алиас: две копии одного типа
 * расходятся молча в момент, когда одну из них поправят, а другую забудут —
 * TypeScript сверяет копию с копией, а не с сервером.
 */
export type SongWordsPayload = TextShareSong;

export type SongWordsResult =
  | { ok: true; payload: SongWordsPayload }
  | { ok: false; reason: 'auth' | 'forbidden' | 'gone' | 'network' };

/**
 * Гостевой пропуск (единый сетлист, фазы 3+4, задача D2) — `?setlist=<id>&g=<key>`,
 * оба параметра сразу или ни одного (`guestWordsAccess`,
 * `apps/share-worker/functions/api/song-texts/[id].ts`: ветка проверяется
 * ТОЛЬКО когда пришли ОБА). Участник за кукой их не передаёт вовсе —
 * `credentials: 'include'` остаётся ЕДИНСТВЕННЫМ пропуском для него, как и
 * было; гостю кука ничего не даёт, и без этих двух параметров маршрут
 * встречает его тем же 401, что и раньше этой задачи.
 */
export interface SongTextGuestAccess { setlistId: string; guestKey: string }

export async function fetchSongText(
  id: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
  guest?: SongTextGuestAccess,
): Promise<SongWordsResult> {
  try {
    const qs = guest ? `?setlist=${encodeURIComponent(guest.setlistId)}&g=${encodeURIComponent(guest.guestKey)}` : '';
    const res = await fetchImpl(`${apiBase}/api/song-texts/${encodeURIComponent(id)}${qs}`, {
      credentials: 'include',
    });
    if (res.status === 401) return { ok: false, reason: 'auth' };
    if (res.status === 403) return { ok: false, reason: 'forbidden' };
    if (res.status === 404) return { ok: false, reason: 'gone' };
    if (!res.ok) return { ok: false, reason: 'network' };
    const payload = await res.json() as SongWordsPayload;
    return { ok: true, payload };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/**
 * Итог ЗАПИСИ — у PATCH и DELETE он общий, потому что общая у них и лестница
 * отказов маршрута: 401 → 404 → 403 `not_owner` → 200.
 *
 * Причины те же четыре, что у чтения, и различаются они по той же причине:
 * переключатель в листе владельца при отказе обязан вернуться в прежнее
 * положение И НАЗВАТЬ причину. «Войдите заново», «это не ваши слова», «их уже
 * нет» и «сервер не ответил» требуют от человека разных действий, и схлопнуть
 * их в одно «не удалось» значит не сказать ему ничего.
 *
 * 400 (`invalid_json` / `invalid_visibility`) в этот разбор не попадает
 * отдельной причиной СОЗНАТЕЛЬНО: тело собирает эта функция, а тип аргумента —
 * `'team' | 'public'`, то есть белый список маршрута повторён здесь системой
 * типов. Пришедшая 400 означала бы расхождение клиента с сервером, а не
 * действие человека; она падает в `network` — «попробуй ещё раз», — и это
 * честнее, чем заводить для невозможного состояния собственную подпись.
 */
export type SongTextWriteResult =
  | { ok: true }
  | { ok: false; reason: 'auth' | 'forbidden' | 'gone' | 'network' };

/** Общий разбор ответа обеих записей — лестница у них одна, и вторая её копия
 *  разошлась бы с первой молча. */
function writeResult(status: number): SongTextWriteResult {
  if (status === 401) return { ok: false, reason: 'auth' };
  if (status === 403) return { ok: false, reason: 'forbidden' };
  if (status === 404) return { ok: false, reason: 'gone' };
  if (status < 200 || status >= 300) return { ok: false, reason: 'network' };
  return { ok: true };
}

/**
 * Переключить, кто видит слова: `PATCH /api/song-texts/:id` с телом
 * `{ visibility }`. Владелец-only (403 `not_owner` товарищу по команде).
 *
 * Тип значения — `'team' | 'public'`, а не `string`: маршрут держит белый
 * список из этих двух строк, и любая третья сегодня тихо сошла бы за
 * «команду». Повторить белый список типом дешевле, чем узнать об ошибке по
 * 400 в бою.
 */
export async function setSongTextVisibility(
  id: string,
  visibility: 'team' | 'public',
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<SongTextWriteResult> {
  try {
    const res = await fetchImpl(`${apiBase}/api/song-texts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ visibility }),
    });
    return writeResult(res.status);
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/**
 * Снять слова с публикации: `DELETE /api/song-texts/:id`. На сервере это
 * штамп `removed_at`, а не удаление строки (см. `softRemoveSongText`), но для
 * читателя разницы нет — слова исчезают из библиотеки команды, и уже
 * разосланная ссылка перестаёт открываться.
 */
export async function removeSongText(
  id: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<SongTextWriteResult> {
  try {
    const res = await fetchImpl(`${apiBase}/api/song-texts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return writeResult(res.status);
  } catch {
    return { ok: false, reason: 'network' };
  }
}
